// Centralized offline configuration
// All limits, thresholds, and TTLs in one place

const SEVEN_DAYS = 7 * 24 * 60 * 60 * 1000;

export const OfflineConfig = {
  // Storage limits
  storage: {
    maxImageStorageBytes: 50 * 1024 * 1024, // 50MB
    maxCacheStorageBytes: 100 * 1024 * 1024, // 100MB
    maxMemoryCacheSize: 100, // number of items
    cleanupThreshold: 0.8, // cleanup when 80% full
  },

  // Cache TTLs (milliseconds)
  // Set to 7 days to match the offline auth grace period — data stays usable
  // for the full duration the user can be offline.
  ttl: {
    // All entity types use the same 7-day TTL to match the offline grace period
    settings: SEVEN_DAYS,
    gyms: SEVEN_DAYS,
    athletes: SEVEN_DAYS,
    trainers: SEVEN_DAYS,
    programs: SEVEN_DAYS,
    diets: SEVEN_DAYS,
    memberships: SEVEN_DAYS,
    daybooks: SEVEN_DAYS,
    attendance: SEVEN_DAYS,
    payments: SEVEN_DAYS,
    lockers: SEVEN_DAYS,
    notifications: SEVEN_DAYS,
    // Default fallback
    default: SEVEN_DAYS,
  },

  // Stale durations (milliseconds) - time before data is considered stale.
  // A shorter stale duration means the app will try to refresh data more eagerly
  // when online, but offline access always serves cached data regardless of staleness.
  stale: {
    settings: 12 * 60 * 60 * 1000, // 12 hours
    gyms: 12 * 60 * 60 * 1000,
    athletes: 15 * 60 * 1000, // 15 minutes
    trainers: 15 * 60 * 1000,
    programs: 15 * 60 * 1000,
    diets: 15 * 60 * 1000,
    daybooks: 2 * 60 * 1000, // 2 minutes
    attendance: 2 * 60 * 1000,
    payments: 2 * 60 * 1000,
    notifications: 30 * 1000, // 30 seconds
    default: 10 * 60 * 1000, // 10 minutes
  },

  // Retry configuration
  retry: {
    maxRetries: 3,
    baseDelay: 1000, // 1 second
    maxDelay: 30000, // 30 seconds
    jitterFactor: 0.3, // 30% jitter
  },

  // Queue configuration
  queue: {
    maxSize: 1000, // max queued mutations
    maxRetryCount: 5,
    retryDelayBase: 2000, // 2 seconds
    deadLetterMaxAge: 7 * 24 * 60 * 60 * 1000, // 7 days
  },

  // Image storage tiers
  images: {
    persistent: ['avatar', 'branding', 'program-cover'], // image types to persist
    disposable: ['media', 'temporary', 'low-priority'], // image types for Cache API
    maxBlobSize: 5 * 1024 * 1024, // 5MB max per blob
    compressionQuality: 0.8,
    resizeMaxWidth: 1920,
    resizeMaxHeight: 1080,
  },

  // Service worker configuration
  serviceWorker: {
    cacheVersion: 'v2',
    staticCacheName: 'gym-static',
    runtimeCacheName: 'gym-runtime',
    imageCacheName: 'gym-images',
    skipWaiting: false, // safer: don't force activate
    clientsClaim: false, // safer: don't force claim
  },

  // Multi-tab coordination
  multiTab: {
    channelName: 'gym-offline-coordination',
    syncInterval: 1000, // 1 second
  },

  // Diagnostics
  diagnostics: {
    enabled: import.meta.env.DEV,
    logLevel: import.meta.env.DEV ? 'debug' : 'error',
    maxLogEntries: 1000,
  },

  // Failure recovery
  recovery: {
    maxRecoveryAttempts: 3,
    recoveryDelay: 5000, // 5 seconds
    corruptionThreshold: 0.1, // 10% corruption triggers recovery
  },
};

// Environment-based overrides
if (import.meta.env.PROD) {
  OfflineConfig.diagnostics.enabled = false;
  OfflineConfig.diagnostics.logLevel = 'error';
  OfflineConfig.serviceWorker.skipWaiting = true; // more aggressive in prod
  OfflineConfig.serviceWorker.clientsClaim = true;
}

// Helper to get TTL for entity type
export function getTTL(entityType) {
  return OfflineConfig.ttl[entityType] || OfflineConfig.ttl.default;
}

// Helper to get stale duration for entity type
export function getStaleDuration(entityType) {
  return OfflineConfig.stale[entityType] || OfflineConfig.stale.default;
}

// Helper to check if image type should be persistent
export function isPersistentImageType(imageType) {
  return OfflineConfig.images.persistent.includes(imageType);
}
